/**
 * Palet Design System KLH/BPLH v2.1 — sumber tunggal untuk Tailwind, NextUI, dan MUI.
 * Sinkron dengan src/styles/tokens.css. Kalau mengubah di sini, ubah juga di sana.
 *
 * CommonJS karena file ini di-require oleh tailwind.config.js (Node) sekaligus
 * di-import oleh src/theme/muiTheme.js (webpack).
 *
 * Catatan skala: DS menetapkan skala penuh 50–900 untuk hijau/biru/oranye.
 * Untuk success dan danger, DS hanya meng-anchor tiga nilai (warna, -bg, -line);
 * shade lain di bawah diinterpolasi dari anchor tersebut dan hanya dipakai untuk
 * varian halus komponen (hover/flat) — bukan untuk teks di atas latar terang.
 */

// — Hijau KLH · PRIMER (anchor logo di 600) —
const green = {
  50: '#F1F8F8', 100: '#DEF2F1', 200: '#B8EAE6', 300: '#7CDED6', 400: '#25D0C2',
  500: '#07887D', 600: '#005952', 700: '#00453F', 800: '#003631', 900: '#002623',
}

// — Biru KLH · SEKUNDER (anchor logo di 500) —
// DS: #147DEF (500) rasio 4.03:1 → hanya untuk teks besar, ikon, dan grafik.
// Tombol dan teks normal WAJIB memakai 600.
const blue = {
  50: '#EFF5FA', 100: '#DAE8F6', 200: '#B1D0F1', 300: '#77B1EE', 400: '#4596ED',
  500: '#147DEF', 600: '#0E66C4', 700: '#0B529D', 800: '#083E77', 900: '#062D56',
}

// — Oranye KLH · SEKUNDER (anchor logo di 500) —
// DS: maksimal ±10% dari halaman; tidak pernah jadi latar teks putih.
const orange = {
  50: '#FCF4EE', 100: '#F9E4D2', 200: '#F8C8A0', 300: '#F7A664', 400: '#FA8F38',
  500: '#F97910', 600: '#D66305', 700: '#AE5104', 800: '#863E03', 900: '#632E03',
}

// — Netral bernuansa teal —
const ink = { 300: '#A6B2AE', 400: '#75847F', 500: '#51625E', 700: '#283835', 900: '#10201D' }

const neutral = {
  line: '#E1E8E6',
  lineStrong: '#C7D2CF',
  surface: '#FFFFFF',
  surface2: '#F5F9F8',
  surface3: '#EBF2F0',
  bg: '#F2F6F5',
}

// — Status semantik (anchor DS) —
const status = {
  success: '#0E7A4E', successBg: '#DCF3E7', successLine: '#9BDDBC',
  warning: '#AE5104', warningBg: '#F9E4D2', warningLine: '#F8C8A0',
  danger: '#C03A2B', dangerBg: '#FBE3E0', dangerLine: '#EFAFA6',
  info: '#0B529D', infoBg: '#DAE8F6', infoLine: '#B1D0F1',
}

// Skala penuh untuk status — dipakai NextUI agar varian flat/light ikut DS.
// Warning memakai skala oranye apa adanya (DS: --warning = --o-700).
const success = {
  50: '#F0FAF5', 100: '#DCF3E7', 200: '#9BDDBC', 300: '#6BC79B', 400: '#37A672',
  500: '#0E7A4E', 600: '#0B6641', 700: '#095234', 800: '#073E28', 900: '#042A1B',
}
const danger = {
  50: '#FDF1F0', 100: '#FBE3E0', 200: '#EFAFA6', 300: '#E2857A', 400: '#D15C4E',
  500: '#C03A2B', 600: '#A32F23', 700: '#85251B', 800: '#661C14', 900: '#47130E',
}

// — Elevasi & radius —
const shadow = {
  1: '0 1px 2px rgba(0,54,49,.06),0 1px 3px rgba(0,54,49,.05)',
  2: '0 2px 6px rgba(0,54,49,.08),0 6px 16px rgba(0,54,49,.07)',
  3: '0 12px 32px rgba(0,54,49,.12)',
}
const radius = { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px', pill: '999px' }

const font = {
  display: ['Plus Jakarta Sans', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'sans-serif'],
  body: ['Inter', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'sans-serif'],
  mono: ['JetBrains Mono', 'SF Mono', 'Consolas', 'monospace'],
}

// — Tata letak (DS §04 Fondasi & §01 Layout) —
const layout = {
  container: '1200px',   // konten publik
  header: '1380px',      // navbar situs
  appContent: '1440px',  // konten app shell (di luar sidebar)
  gutter: '24px',
  sidebar: '264px',
  sidebarMini: '76px',
  topbar: '62px',
  touchTarget: '44px',
}

// Breakpoint resmi DS — minor yang diizinkan hanya 640 dan 900.
const screens = { sm: '640px', md: '768px', lg: '1024px', xl: '1280px' }

module.exports = {
  green, blue, orange, ink, neutral, status, success, danger,
  shadow, radius, font, layout, screens,
}
